import { useState } from 'react';
import type { ProfilePhoto } from '@/domain/types';
import styles from './PhotoFrame.module.css';

interface PhotoFrameProps {
  photo: ProfilePhoto;
  /** Above-the-fold images should not be lazy. */
  priority?: boolean;
  className?: string;
}

/**
 * Renders a profile photograph, or an honest placeholder when there is not one
 * yet.
 *
 * Three deliberate choices:
 *
 *  - A placeholder is abstract. No stock person, no AI-generated face, no
 *    silhouette. Nobody should ever look at this page and think they have seen
 *    what James looks like when they have not.
 *  - The frame reserves its aspect ratio, so the page does not lurch as images
 *    load on a phone with poor signal.
 *  - If a real file 404s, it degrades to a quiet note rather than a broken
 *    image icon.
 */
export function PhotoFrame({ photo, priority = false, className }: PhotoFrameProps) {
  const [failed, setFailed] = useState(false);

  const showPlaceholder = photo.status === 'placeholder' || !photo.src;

  return (
    <figure
      className={`${styles.frame} ${className ?? ''}`}
      style={{ aspectRatio: String(photo.ratio) }}
    >
      {showPlaceholder ? (
        <div className={styles.placeholder}>
          <div className={styles.placeholderGrid} aria-hidden="true" />
          <div className={styles.placeholderLabel}>
            <span className={styles.placeholderTitle}>Photo coming</span>
            <span className={styles.placeholderHint}>{photo.placeholderHint}</span>
          </div>
        </div>
      ) : failed ? (
        <div className={styles.broken}>Photo unavailable</div>
      ) : (
        <picture>
          {photo.srcWebp ? <source srcSet={photo.srcWebp} type="image/webp" /> : null}
          <img
            className={styles.image}
            src={photo.src}
            alt={photo.alt}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            decoding="async"
            onError={() => setFailed(true)}
          />
        </picture>
      )}
    </figure>
  );
}
